import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { EntityStore, StoreObject } from '@apollo/client/cache';
import { ApolloProvider } from '@apollo/client/react';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';

import { getApolloClient } from '../application/graphql-client';
import { LOCALES } from '../constants/locale';

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;
let apolloLocale: string | undefined;

export const getClient = ({ locale = LOCALES[0], ssrCache }: { locale?: string; ssrCache?: NormalizedCacheObject } = {}) => {
  if (!apolloClient || apolloLocale !== locale) {
    apolloClient = getApolloClient(locale);
    apolloLocale = locale;
  }

  if (ssrCache) {
    const entityStore = (apolloClient.cache as unknown as { data: EntityStore }).data;
    if (entityStore) {
      // Extract of cache.replace code without the clear old cache part
      // https://github.com/apollographql/apollo-client/blob/83935e8e1ea2c3eb4a0f10fffbbfb4d51cfc02d2/src/cache/inmemory/entityStore.ts#L335
      // eslint-disable-next-line eslint-compat/naming-convention
      const { __META, ...rest } = ssrCache;
      Object.keys(rest).forEach(dataId => {
        entityStore.merge(dataId, rest[dataId] as StoreObject);
      });
      if (__META) __META.extraRootIds.forEach(id => entityStore.retain(id));
    } else {
      apolloClient.cache.restore(ssrCache);
    }
  }

  return apolloClient;
};

/**
 * Wraps children in ApolloProvider and restores cache from SSR if present.
 */
const ApolloWrapper: React.FC<{ children: React.ReactNode; ssrCache?: NormalizedCacheObject }> = ({ children, ssrCache }) => {
  const { locale } = useRouter();
  const client = useMemo(() => getClient({ locale, ssrCache }), [locale, ssrCache]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloWrapper;
